import type { CSSProperties } from 'react';
import type { Photo, PhotoAdj } from '../model/types';
import { clampN } from './util';

export const ADJ_DEFAULT: PhotoAdj = { x: 50, y: 50, z: 1, fit: 'fill' };

/** The framing saved on a photo, with anything missing or out of range repaired. */
export function adjOf(p: Photo | null | undefined): PhotoAdj {
  const a: Partial<PhotoAdj> = (p && p.adj) || {};
  return { x: clampN(a.x, 0, 100, 50), y: clampN(a.y, 0, 100, 50), z: clampN(a.z, 1, 3, 1), fit: a.fit === 'whole' ? 'whole' : 'fill' };
}

export function isDefaultAdj(a: PhotoAdj) {
  return a.fit === 'fill' && Math.abs(a.x - 50) < 0.5 && Math.abs(a.y - 50) < 0.5 && a.z < 1.005;
}

export function adjStyle(a: PhotoAdj): CSSProperties {
  if (a.fit === 'whole') return { objectFit: 'contain', objectPosition: '50% 50%' };
  const pos = `${a.x.toFixed(1)}% ${a.y.toFixed(1)}%`;
  return a.z > 1.001 ? { objectPosition: pos, transform: `scale(${a.z.toFixed(3)})`, transformOrigin: pos } : { objectPosition: pos };
}

/**
 * The address to load a photo from. Only photos uploaded to Cloudinary (and, in demo mode, photos kept in the
 * browser) are shown, so a website can't make guests' phones load images from anywhere else.
 * Cloudinary photos get a size limit and automatic format and quality, so phones download a small file.
 */
export function photoUrl(p: Photo | null | undefined, width = 1600): string {
  const u = p && typeof p.url === 'string' ? p.url : '';
  if (/^data:image\/(jpeg|png|webp|gif);base64,/.test(u) || u.startsWith('blob:')) return u;
  const m = /^(https:\/\/res\.cloudinary\.com\/[a-z0-9_-]+\/image\/upload\/)([^?#"'\s]+)$/i.exec(u);
  if (!m) return '';
  if (!/^[a-z]_[^/]*\//.test(m[2])) return `${m[1]}f_auto,q_auto,c_limit,w_${Math.round(width)}/${m[2]}`;
  return u;
}

export const hasPhoto = (p: Photo | null | undefined): p is Photo => !!photoUrl(p);
